import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import Footer from "../ui/Footer";
import GrowthGlyph, { GrowthStage } from "../ui/GrowthGlyph";
import JourneyPlant from "../ui/JourneyPlant";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchRoute } from "../../queries/contentQueries";

interface MainLayoutProps {
  children?: ReactNode;
}

interface JourneyStep {
  name: string;
  path: string;
  stage: GrowthStage;
  /** Growth stage, e.g. "Seed" */
  hint: string;
  /** One line telling a first-time visitor what this step is */
  blurb: string;
}

// Ordered as a growth journey: learn → build → give back → who I am now → full record
const journey: JourneyStep[] = [
  { name: "Learn", path: "/writing", stage: 0, hint: "Seed", blurb: "Where it starts: what I learn, written down" },
  { name: "Build", path: "/projects", stage: 1, hint: "Sprout", blurb: "Knowledge put to work, for clients and for me" },
  { name: "Give Back", path: "/open-source", stage: 2, hint: "Sapling", blurb: "Growing for others: open-source tools" },
  { name: "Meet Me", path: "/", stage: 3, hint: "Tree", blurb: "A snapshot of who I am today" },
  { name: "Résumé", path: "/resume", stage: 4, hint: "Fruit", blurb: "The results: experience, skills, education" },
];

// Caption shown when the tour reaches the "Let's Talk" button
const harvest = { hint: "Harvest", blurb: "Like what grew here? Let's talk." };

const CONTACT_INDEX = journey.length;
const TOUR_SEEN_KEY = "journey-tour-seen";
const TOUR_START_MS = 2800;
const TOUR_STEP_MS = 2000;
// Grace period before the caption hides, so moving between pills doesn't blink it
const CAPTION_HIDE_MS = 160;
const SLIDE = { type: "spring", stiffness: 380, damping: 34 } as const;

const hasSeenTour = () => {
  try {
    return localStorage.getItem(TOUR_SEEN_KEY) === "1";
  } catch {
    return true;
  }
};

const markTourSeen = () => {
  try {
    localStorage.setItem(TOUR_SEEN_KEY, "1");
  } catch {
    // Storage blocked: the tour simply plays again next visit
  }
};

interface JourneyPillProps extends JourneyStep {
  index: number;
  isActive: boolean;
  isTouring: boolean;
  /** Hovered, focused or being toured: shows the sliding ghost highlight */
  isFocused: boolean;
  /** This pill holds the ghost highlight (it stays put while fading out) */
  hasGhost: boolean;
  /** Ghost is moving between pills rather than appearing fresh */
  ghostSlides: boolean;
  /** Changes on each fresh appearance so the ghost never slides in from a stale spot */
  ghostId: string;
  pillRef: (el: HTMLAnchorElement | null) => void;
  onPreview: (index: number | null) => void;
}

const JourneyPill: React.FC<JourneyPillProps> = ({
  name,
  path,
  stage,
  index,
  isActive,
  isTouring,
  isFocused,
  hasGhost,
  ghostSlides,
  ghostId,
  pillRef,
  onPreview,
}) => {
  const reduceMotion = useReducedMotion();
  // Bumped each time this step is highlighted so its glyph grows again
  const [growKey, setGrowKey] = useState(0);
  const isLit = isActive || isTouring;
  const wasLit = useRef(isLit);

  useEffect(() => {
    if (isLit && !wasLit.current) setGrowKey((k) => k + 1);
    wasLit.current = isLit;
  }, [isLit]);

  return (
    <Link
      ref={pillRef}
      to={path}
      aria-label={name}
      aria-current={isActive ? "page" : undefined}
      className={`nav-pill ${isActive ? "nav-pill--active" : ""} ${isFocused && !isActive ? "nav-pill--focused" : ""}`}
      onMouseEnter={() => onPreview(index)}
      onMouseLeave={() => onPreview(null)}
      onFocus={() => onPreview(index)}
      onBlur={() => onPreview(null)}
    >
      {isActive && (
        <motion.span
          layoutId="nav-active-pill"
          className="nav-pill__active-bg"
          transition={reduceMotion ? { duration: 0 } : SLIDE}
        />
      )}
      {hasGhost && !isActive && (
        <motion.span
          layoutId={ghostId}
          className="nav-pill__ghost-bg"
          // Remounts on every move; keep full opacity while sliding so it never blinks
          initial={ghostSlides ? false : { opacity: 0 }}
          animate={{ opacity: isFocused ? 1 : 0 }}
          transition={reduceMotion ? { duration: 0 } : { ...SLIDE, opacity: { duration: 0.2 } }}
        />
      )}
      <GrowthGlyph
        key={growKey}
        stage={stage}
        // Stagger the first paint left to right so the nav "grows" seed → fruit
        delay={growKey === 0 ? 0.25 + index * 0.18 : 0.05}
        className="nav-pill__icon"
      />
      <span className="nav-pill__label">{name}</span>
    </Link>
  );
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const queryClient = useQueryClient();

  const navRef = useRef<HTMLElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<(HTMLElement | null)[]>([]);

  // Pill centres: relative to the nav (for the caption) and to the group (for the vine)
  const [centers, setCenters] = useState<{ nav: number[]; group: number[] }>({ nav: [], group: [] });
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [tourIndex, setTourIndex] = useState<number | null>(null);
  // Where the caption sits and whether it shows; the index outlives hover so it can fade in place
  const [caption, setCaption] = useState<{ index: number; visible: boolean }>({ index: 0, visible: false });
  const captionWasVisible = useRef(false);
  const [ghostSession, setGhostSession] = useState(0);
  const [isCompact, setIsCompact] = useState(false);

  const isActivePath = (path: string) =>
    path === "/" ? location.pathname === path : location.pathname.startsWith(path);

  const activeIndex = isActivePath("/contact") ? CONTACT_INDEX : journey.findIndex((s) => isActivePath(s.path));

  const measure = useCallback(() => {
    const nav = navRef.current?.getBoundingClientRect();
    const group = groupRef.current?.getBoundingClientRect();
    if (!nav || !group) return;
    const mids = pillRefs.current.map((el) => {
      const r = el?.getBoundingClientRect();
      return r ? r.left + r.width / 2 : 0;
    });
    setCenters({ nav: mids.map((x) => x - nav.left), group: mids.map((x) => x - group.left) });
  }, []);

  useLayoutEffect(() => {
    measure();
    const observer = new ResizeObserver(measure);
    if (navRef.current) observer.observe(navRef.current);
    pillRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [measure]);

  const endTour = useCallback(() => {
    setTourIndex(null);
    markTourSeen();
  }, []);

  // First visit: walk through every step once so the flow explains itself
  useEffect(() => {
    if (hasSeenTour()) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i <= CONTACT_INDEX; i++) {
      timers.push(setTimeout(() => setTourIndex(i), TOUR_START_MS + i * TOUR_STEP_MS));
    }
    timers.push(setTimeout(endTour, TOUR_START_MS + (CONTACT_INDEX + 1) * TOUR_STEP_MS + 600));

    // Any deliberate interaction hands control back to the visitor
    const stop = () => {
      timers.forEach(clearTimeout);
      endTour();
    };
    window.addEventListener("pointerdown", stop, { once: true });
    window.addEventListener("keydown", stop, { once: true });
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("pointerdown", stop);
      window.removeEventListener("keydown", stop);
    };
  }, [endTour]);

  const liveIndex = previewIndex ?? tourIndex;

  useEffect(() => {
    if (liveIndex !== null) {
      if (!captionWasVisible.current) setGhostSession((n) => n + 1);
      setCaption({ index: liveIndex, visible: true });
      return;
    }
    const timer = setTimeout(() => setCaption((c) => ({ ...c, visible: false })), CAPTION_HIDE_MS);
    return () => clearTimeout(timer);
  }, [liveIndex]);

  useEffect(() => {
    captionWasVisible.current = caption.visible;
  });

  // Below lg the caption spans the nav instead of following a pill
  useEffect(() => {
    const query = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsCompact(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const handlePreview = (index: number | null) => {
    if (index !== null && tourIndex !== null) endTour();
    // Hover/focus signals intent: load that page's data before the click
    if (index !== null) prefetchRoute(queryClient, index === CONTACT_INDEX ? "/contact" : journey[index].path);
    setPreviewIndex(index);
  };

  const captionStep = caption.index === CONTACT_INDEX ? harvest : journey[caption.index];
  // Slide between pills only while already showing; a fresh caption appears in place
  const captionSlides = captionWasVisible.current && caption.visible && !reduceMotion;
  const focusIndex = caption.visible ? caption.index : null;

  // The vine grows to the step being toured, otherwise to the current page
  const vineTarget = tourIndex ?? activeIndex;
  const vineIndex = vineTarget < 0 ? null : Math.min(vineTarget, journey.length - 1);
  const vineStart = centers.group[0];
  const vineEnd = centers.group[journey.length - 1];
  const vineReady = vineStart !== undefined && vineEnd !== undefined;
  const vineWidth = vineReady && vineIndex !== null ? centers.group[vineIndex] - vineStart : 0;

  return (
    <div className="flex flex-col min-h-screen pb-24 lg:pb-0">
      {/* Floating navigation */}
      <nav ref={navRef} className="floating-nav" aria-label="Main navigation">
        <div ref={groupRef} className="floating-nav__group">
          {vineReady && (
            <div
              className="journey-vine"
              style={{ left: vineStart, width: vineEnd - vineStart }}
              aria-hidden="true"
            >
              <motion.div
                className="journey-vine__fill"
                initial={false}
                animate={{ width: vineWidth }}
                transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 70, damping: 18 }}
              >
                {/* Pulsing tip: hints that the journey continues to the next step */}
                {vineTarget >= 0 && vineTarget < CONTACT_INDEX && <span className="journey-vine__tip" />}
              </motion.div>
            </div>
          )}

          {journey.map((step, index) => (
            <JourneyPill
              key={step.path}
              {...step}
              index={index}
              isActive={index === activeIndex}
              isTouring={index === tourIndex}
              isFocused={index === focusIndex}
              hasGhost={index === caption.index}
              ghostSlides={captionSlides}
              ghostId={`nav-ghost-pill-${ghostSession}`}
              pillRef={(el) => {
                pillRefs.current[index] = el;
              }}
              onPreview={handlePreview}
            />
          ))}
        </div>

        <Link
          ref={(el) => {
            pillRefs.current[CONTACT_INDEX] = el;
          }}
          to="/contact"
          aria-label="Let's Talk"
          aria-current={activeIndex === CONTACT_INDEX ? "page" : undefined}
          className={`nav-pill nav-pill--cta ${tourIndex === CONTACT_INDEX ? "nav-pill--cta-touring" : ""}`}
          onMouseEnter={() => handlePreview(CONTACT_INDEX)}
          onMouseLeave={() => handlePreview(null)}
          onFocus={() => handlePreview(CONTACT_INDEX)}
          onBlur={() => handlePreview(null)}
        >
          {/* Below lg the CTA becomes a compact tab: chat icon over a short label */}
          <MessageCircle className="nav-pill__cta-chat" aria-hidden="true" strokeWidth={1.8} />
          <span className="nav-pill__cta-full">Let's Talk</span>
          <span className="nav-pill__cta-short" aria-hidden="true">
            Talk
          </span>
          <span className="nav-pill__cta-icon" aria-hidden="true">
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </span>
        </Link>

        {/* Caption for the previewed / toured step */}
        <motion.div
          className="journey-caption"
          initial={false}
          animate={{
            x: isCompact ? 0 : centers.nav[caption.index] ?? 0,
            opacity: caption.visible ? 1 : 0,
            y: caption.visible ? 0 : -6,
          }}
          transition={{
            x: captionSlides ? SLIDE : { duration: 0 },
            opacity: { duration: reduceMotion ? 0 : 0.2 },
            y: { duration: reduceMotion ? 0 : 0.2 },
          }}
          aria-hidden="true"
        >
          <div className="journey-caption__anchor">
            <div className="journey-caption__card">
              {/* Stays mounted so it keeps growing (or shrinking) between steps */}
              <JourneyPlant
                stage={Math.min(caption.index, journey.length - 1)}
                fresh={!captionSlides}
                className="journey-caption__plant"
              />
              <motion.div
                key={caption.index}
                className="journey-caption__text"
                initial={captionSlides ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.18 }}
              >
                <span className="journey-caption__step">
                  {caption.index === CONTACT_INDEX ? "→" : String(caption.index + 1).padStart(2, "0")} ·{" "}
                  {captionStep.hint}
                </span>
                <span className="journey-caption__blurb">{captionStep.blurb}</span>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </nav>

      <main className="flex-grow">{children || <Outlet />}</main>

      <Footer />
    </div>
  );
};

export default MainLayout;
